import { Component, ChangeDetectionStrategy } from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatFormField, MatInput} from "@angular/material/input";
import {MatOption, MatSelect} from "@angular/material/select";
import {PipelinesFilterService} from "../pipelines-filter.service";
import {PipelinesService} from "../../../pipelines.service";
import {AsyncPipe} from "@angular/common";
import {MatIcon} from "@angular/material/icon";

@Component({
  selector: 'pipelines-sort-block',
  standalone: false,
  templateUrl: './pipelines-sort-block.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './pipelines-sort-block.component.scss',
})
export class PipelinesSortBlockComponent {

  constructor(public pipeFilterService: PipelinesFilterService, public pipelineService: PipelinesService) {
  }

  public sortGroups:FormGroup[] = []

  public addSortParameters(){
    if(!this._validateSortParameters() && this.sortGroups.length>0){
      return;
    }
    let formGroup = new FormGroup({
      field: new FormControl(),
      value: new FormControl(),
      _id: new FormControl(crypto.randomUUID()),
      children: new FormArray([
        new FormGroup({
          field: new FormControl(),
          value: new FormControl()
        })
      ])
    })
    this.sortGroups.push(formGroup)
  }

  public addSubSortParameters(group: FormGroup){
    if(!this._validateChildSortParameters(group)){
      return;
    }
    let children: FormArray = group.get('children') as FormArray
    children.push(new FormGroup({
      field: new FormControl(),
      value: new FormControl(),
    }))
  }

  public compareObjects(o1: any, o2: any): boolean {
    if (!o1 || !o2) return o1 === o2;
    if (typeof o1 === 'string') return o1 === o2.name;
    if (typeof o2 === 'string') return o2 === o1.name;
    return o1.id === o2.id || o1.name === o2.name;
  }


  private _validateSortParameters() {
    const hasValue = (c: any): boolean => {
      const hasOwnValue = c.value.value !== null && c.value.value !== '';

      const hasChildValue =
        c.children?.value?.some((child: any) =>
          child.value !== null && child.value !== ''
        );
      return hasOwnValue || hasChildValue;
    };

    return this.sortGroups.every(field => hasValue(field.controls));
  }

  public removeSortCondition(group: FormGroup){
    this.sortGroups = this.sortGroups.filter((sortForm:FormGroup) => sortForm !== group)
    this.pipelineService.resetSelections()
  }

  private _validateChildSortParameters(group: FormGroup){
    const children: FormArray = group.get('children') as FormArray
    if (!children || children.length === 0) {
      return true;
    }
    return children.controls.every(child =>
      Object.entries((child as FormGroup).controls)
        .every(([key, control]) => control.value !== null && control.value !== '')
    );
  }


  public resolveFromSaved(sort: any[], sortFields: any[]){
    this.sortGroups = []
    sort.forEach((entry: any) => {
      const fieldDef = sortFields.find((f: any) => f.field === entry.field)

      if (fieldDef?.special_lookup) {
        this.pipelineService.getModelLookup(fieldDef.model, fieldDef.field);
      }
      const isJsonType = entry.sort_type === 'json' || Array.isArray(entry.sort);
      if (!isJsonType) {
        this.sortGroups.push(new FormGroup({
          field: new FormControl(fieldDef ?? null),
          value: new FormControl(this.pipeFilterService.sortFromAbbrv(entry.sort)),
          children: new FormArray<FormGroup>([])
        }))
      } else {
        if (fieldDef?.special_lookup) {
          // Find the separated filter and value objects in the array
          const filterEntry = (entry.sort ?? []).find((s: any) => s.sort_type === 'filter');
          const valueEntry = (entry.sort ?? []).find((s: any) => s.sort_type === 'value');

          // Create a single Form Group combining both
          const children = [
            new FormGroup({
              // Set an object with 'name' so your compareObjects function will successfully bind it
              // to the asynchronously loaded model option (e.g., "Prospect Priority")
              field: new FormControl(filterEntry ? { name: filterEntry.value ?? filterEntry.sort } : null),
              // Bind the sort direction (e.g., "ASC")
              value: new FormControl(valueEntry ? this.pipeFilterService.sortFromAbbrv(valueEntry.sort) : null)
            })
          ];

          this.sortGroups.push(new FormGroup({
            field: new FormControl(fieldDef ?? null),
            value: new FormControl(),
            children: new FormArray<FormGroup>(children)
          }));
        } else {
          const reorderedSort = [...(entry.sort ?? [])].sort((a: any, b: any) => {
            if (a.sort_type === 'value') return -1;
            if (b.sort_type === 'value') return 1;
            return 0;
          });
          const children = reorderedSort.map((c: any) => {
            const childDef = (fieldDef?.children ?? []).find((cf: any) =>
              cf.field === c.field || cf.id_field === c.field)

            if (childDef?.is_sortable && childDef?.sort_type === 'filter') {
              this.pipelineService.getModelLookup(childDef.model, childDef.field, false, true);
            }
            return new FormGroup({
              field: new FormControl(childDef ?? null),
              value: new FormControl(
                c.sort_type === 'value'
                  ? this.pipeFilterService.sortFromAbbrv(c.sort)
                  : c.value ?? c.sort)
            })
          })
          this.sortGroups.push(new FormGroup({
            field: new FormControl(fieldDef ?? null),
            value: new FormControl(),
            children: new FormArray<FormGroup>(children)
          }))

        }
      }
    })
  }
  public onSelectionChange(event: any, group: FormGroup){
    if(event.value.special_lookup){
      this.pipelineService.getModelLookup(event.value.model,event.value.field)
    }
    if(event.value.is_sortable && event.value.sort_type==='filter'){
      this.pipelineService.getModelLookup(event.value.model, event.value.field, false, true)
    }
  }


  public clearSortData(){
    this.sortGroups = []
    this.pipelineService.resetSelections()
  }

}
